import type { TradeName } from '../types/companyValues'
import { getPrimaryTradeName } from './getPrimaryTradeName'

export interface PrimaryTradeNameContact {
  contactEmail?: string
  contactFirstName?: string
  contactLastName?: string
  contactPhone?: string
}

// Updates the primary trade name's contact and rebuilds name.full from the first and last name.
// Every other trade name is returned unchanged.
export const getTradeNamesWithUpdatedPrimaryContact = (
  tradeNames: TradeName[],
  contact: PrimaryTradeNameContact,
): TradeName[] => {
  const primaryTradeName = getPrimaryTradeName(tradeNames)
  const { contactEmail, contactFirstName, contactLastName, contactPhone } = contact

  return tradeNames.map((tradeName) => {
    if (tradeName !== primaryTradeName) {
      return tradeName
    }

    // The stored contact can lack `name`, but a write must include `first` and `last`, so fall back to ''.
    const storedName = tradeName.contact.name
    const firstName = contactFirstName ?? storedName?.first ?? ''
    const lastName = contactLastName ?? storedName?.last ?? ''
    const fullName = [firstName, lastName].filter(Boolean).join(' ')

    return {
      ...tradeName,
      contact: {
        ...tradeName.contact,
        email: contactEmail ?? tradeName.contact.email,
        name: {
          ...storedName,
          first: firstName,
          // A blank first/last (empty input, or legacy data that never populated them) must not
          // blank out an existing full name; keep it until a non-empty first/last replaces it.
          full: fullName || storedName?.full,
          last: lastName,
        },
        phone: contactPhone ?? tradeName.contact.phone,
      },
    }
  })
}
